import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { UploadedFile } from "./FileUploadZone";
import { FileVideo, FileAudio, FileImage, FileText, File } from "lucide-react";
import ShareLinkModal from "@/components/ShareLinkModal";

interface FileListProps {
  files: UploadedFile[];
  loadRecentShares: () => void;
  clearUploadedFiles: () => void;
}

const FileList = ({
  files,
  loadRecentShares,
  clearUploadedFiles,
}: FileListProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "mp4":
      case "mov":
      case "avi":
        return <FileVideo className="h-8 w-8 text-blue-500" />;
      case "mp3":
      case "wav":
        return <FileAudio className="h-8 w-8 text-purple-500" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileImage className="h-8 w-8 text-green-500" />;
      case "pdf":
        return <FileText className="h-8 w-8 text-red-500" />; // Changed from FilePdf to FileText
      default:
        return <File className="h-8 w-8 text-gray-500" />; // Changed from FileText to File for default case
    }
  };

  const onShareCreated = () => {
    setIsShareModalOpen(false);
    loadRecentShares();
  };

  if (files.length === 0) return null;

  return (
    <>
      <div className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium text-gray-900">Uploaded Files</h3>

          <div className="flex justify-between items-end">
            <Button
              variant="outline"
              className="mr-2"
              onClick={() => clearUploadedFiles()}
            >
              Clear
            </Button>
            <Button onClick={() => setIsShareModalOpen(true)}>
              Share All Files
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm"
            >
              <div className="flex items-center flex-1 min-w-0 mr-4">
                {getFileIcon(file.name)}
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                    {typeof file?.progress === "number" && (
                      <p className="text-sm text-gray-500">{file.progress}%</p>
                    )}
                  </div>
                </div>
              </div>
              {typeof file.progress === "number" && file.progress < 100 && (
                <div className="relative h-8 w-8">
                  <svg
                    className="transform -rotate-90 h-8 w-8"
                    viewBox="0 0 32 32"
                  >
                    <circle
                      cx="16"
                      cy="16"
                      r="14"
                      className="stroke-current text-gray-200"
                      strokeWidth="3"
                      fill="none"
                    />
                    <circle
                      cx="16"
                      cy="16"
                      r="14"
                      className="stroke-current text-blue-500"
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 14}`}
                      strokeDashoffset={`${
                        2 * Math.PI * 14 * (1 - file.progress / 100)
                      }`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ShareLinkModal
        isOpen={isShareModalOpen}
        files={files}
        onClose={() => onShareCreated()}
      />
    </>
  );
};

export default FileList;
